import { useState, useEffect } from "react";
import { useSowingInstructionsByStatus } from "../hooks/useSowingInstructions";
import { useSowingsByInstruction, useAssignChamberPlacements, useFinalizeGermination } from "../hooks/useSowingLifecycle";
import { useGerminationChambers } from "../hooks/useSowingInstructions";
import type { SowingInstruction } from "../services/sowingInstructionsApi";
import type { SowingDto } from "../services/sowingLifecycleApi";
import { RecordGerminationWithPreviewDialog } from "../components/sowingInstructions/PhaseWorkflowDialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNotification } from "@/shared/hooks/useNotification";

const SOWING_INSTRUCTION_STATUSES = [
  { value: 0, label: "Pending" },
  { value: 1, label: "Issued" },
  { value: 2, label: "Sown" },
  { value: 3, label: "Germinated" },
  { value: 4, label: "Growing" },
  { value: 5, label: "Ready for Dispatch" },
  { value: 6, label: "Stored" },
  { value: 7, label: "Dispatched" },
  { value: 8, label: "Overgrown" },
  { value: 9, label: "Revoked" },
  { value: 10, label: "On Hold" },
  { value: 11, label: "Ready for Growing" },
  { value: 12, label: "Draft" },
  { value: 13, label: "Partially Sown" },
];

// ─── Assign Chamber Dialog ────────────────────────────────────────────────────

function AssignChamberDialog({
  sowing,
  open,
  onOpenChange,
  onSuccess,
}: {
  sowing: SowingDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const [placements, setPlacements] = useState<{ chamberId: string; trays: string }[]>([
    { chamberId: "", trays: "" },
  ]);
  const chambersQuery = useGerminationChambers();
  const assignMutation = useAssignChamberPlacements();
  const notify = useNotification();

  const handleSubmit = () => {
    const validPlacements = placements.filter(
      (p) => p.chamberId && parseFloat(p.trays) > 0
    );
    if (validPlacements.length === 0) {
      notify.error("Add at least one chamber placement");
      return;
    }

    const total = validPlacements.reduce((sum, p) => sum + parseFloat(p.trays), 0);
    if (total > sowing.unassignedTrays) {
      notify.error(
        `Total placements (${total}) exceeds unassigned trays (${sowing.unassignedTrays})`
      );
      return;
    }

    assignMutation.mutate(
      {
        sowingId: sowing.id,
        placements: validPlacements.map((p) => ({
          germinationChamberId: p.chamberId,
          traysPlaced: parseFloat(p.trays),
        })),
      },
      {
        onSuccess: () => {
          notify.success("Chamber placements assigned");
          onSuccess();
        },
        onError: (error: any) => {
          notify.error(
            error.response?.data?.message || "Failed to assign chambers"
          );
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Chamber Placements</DialogTitle>
          <DialogDescription>
            Sowing: {sowing.traysSown} trays on{" "}
            {new Date(sowing.sowingDate).toLocaleDateString()} —{" "}
            {sowing.unassignedTrays} trays unassigned
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {placements.map((placement, idx) => (
            <div key={idx} className="flex gap-2 items-end">
              <div className="flex-1">
                <Select
                  value={placement.chamberId}
                  onValueChange={(value) => {
                    const updated = [...placements];
                    updated[idx] = { ...updated[idx], chamberId: value };
                    setPlacements(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select chamber" />
                  </SelectTrigger>
                  <SelectContent>
                    {chambersQuery.data
                      ?.filter((c) => c.isActive)
                      .map((chamber) => (
                        <SelectItem key={chamber.id} value={chamber.id}>
                          {chamber.chamberName} ({chamber.chamberCode})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Trays"
                  value={placement.trays}
                  onChange={(e) => {
                    const updated = [...placements];
                    updated[idx] = { ...updated[idx], trays: e.target.value };
                    setPlacements(updated);
                  }}
                />
              </div>
              {placements.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPlacements(placements.filter((_, i) => i !== idx))}
                >
                  ✕
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setPlacements([...placements, { chamberId: "", trays: "" }])
            }
          >
            + Add Chamber
          </Button>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={assignMutation.isPending}>
              {assignMutation.isPending ? "Assigning..." : "Assign"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Expandable Sowing Events ─────────────────────────────────────────────────

function SowingEventsPanel({
  instruction,
  onRecordGermination,
  onFinalized,
}: {
  instruction: SowingInstruction;
  onRecordGermination: (sowing: SowingDto) => void;
  onFinalized?: () => void;
}) {
  const sowingsQuery = useSowingsByInstruction(instruction.id);
  const [assignSowing, setAssignSowing] = useState<SowingDto | null>(null);
  const finalizeMutation = useFinalizeGermination();
  const [finalizing, setFinalizing] = useState(false);

  const sowings = sowingsQuery.data ?? [];
  const eligibleSowings = sowings.filter(
    (s) => s.status === "Sown" || s.status === "Germinating"
  );
  const totalSown = sowings.reduce((sum, s) => sum + s.traysSown, 0);
  const totalGerminated = sowings.reduce((sum, s) => sum + s.traysGerminated, 0);
  const unsownTrays = instruction.quantityToSow - totalSown;

  // All existing sowing batches closed AND the full target quantity has been sown
  const allClosed =
    sowings.length > 0 &&
    sowings.every((s) => s.status === "Closed") &&
    totalSown >= instruction.quantityToSow;

  // Existing batches are all closed but not all target trays have been sown yet
  const batchesClosedPartially =
    sowings.length > 0 &&
    sowings.every((s) => s.status === "Closed") &&
    totalSown < instruction.quantityToSow;

  // Auto-finalize stuck instructions (all sowings closed + fully sown but SI still on this page)
  useEffect(() => {
    if (allClosed && !finalizing && !finalizeMutation.isSuccess) {
      setFinalizing(true);
      finalizeMutation.mutate(instruction.id, {
        onSuccess: (finalized) => {
          if (finalized && onFinalized) onFinalized();
        },
        onSettled: () => setFinalizing(false),
      });
    }
  }, [allClosed, instruction.id]);

  if (sowingsQuery.isLoading)
    return <p className="text-sm text-gray-500 py-2 px-4">Loading sowings...</p>;

  if (batchesClosedPartially)
    return (
      <div className="py-3 px-4 text-sm space-y-1">
        <p className="text-amber-700 font-medium">
          Sowing batches recorded so far are fully germinated, but the instruction target is not yet met.
        </p>
        <p className="text-gray-600">
          Germinated: <span className="font-semibold">{totalGerminated}</span> trays &nbsp;·&nbsp;
          Not yet sown: <span className="font-semibold">{unsownTrays}</span> trays
          &nbsp;(target: {instruction.quantityToSow})
        </p>
        <p className="text-gray-500 text-xs">
          Record the remaining sowing batches to continue germination tracking.
        </p>
      </div>
    );

  if (allClosed)
    return (
      <div className="py-3 px-4 text-sm">
        {finalizeMutation.isPending ? (
          <p className="text-blue-600">Finalizing germination — moving to Ready for Growing...</p>
        ) : finalizeMutation.isSuccess ? (
          <p className="text-green-600">Germination finalized — instruction moved to Ready for Growing.</p>
        ) : (
          <p className="text-amber-600">All sowing batches are fully germinated. Finalizing...</p>
        )}
      </div>
    );

  if (eligibleSowings.length === 0)
    return (
      <p className="text-sm text-gray-500 py-2 px-4">
        No sowing events ready for germination.
      </p>
    );

  return (
    <div className="border-t bg-gray-50/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="py-2 px-4 font-medium">Sowing Date</th>
            <th className="py-2 px-4 font-medium">Trays Sown</th>
            <th className="py-2 px-4 font-medium">Chambers</th>
            <th className="py-2 px-4 font-medium">Germinated</th>
            <th className="py-2 px-4 font-medium">Status</th>
            <th className="py-2 px-4 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {eligibleSowings.map((sowing) => (
            <tr key={sowing.id} className="border-b last:border-0">
              <td className="py-2 px-4">
                {new Date(sowing.sowingDate).toLocaleDateString()}
              </td>
              <td className="py-2 px-4">{sowing.traysSown}</td>
              <td className="py-2 px-4">
                {sowing.chamberPlacements.length > 0 ? (
                  <span className="text-green-700">
                    {sowing.chamberPlacements
                      .map((p) => `${p.chamberName} (${p.traysPlaced})`)
                      .join(", ")}
                  </span>
                ) : (
                  <span className="text-amber-600">Unassigned</span>
                )}
                {sowing.unassignedTrays > 0 && sowing.chamberPlacements.length > 0 && (
                  <span className="text-amber-600 ml-1">
                    (+{sowing.unassignedTrays} unassigned)
                  </span>
                )}
              </td>
              <td className="py-2 px-4">
                {sowing.traysGerminated} / {sowing.traysSown}
              </td>
              <td className="py-2 px-4">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    sowing.status === "Germinating"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {sowing.status}
                </span>
              </td>
              <td className="py-2 px-4 space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRecordGermination(sowing)}
                >
                  Record Germination
                </Button>
                {sowing.unassignedTrays > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setAssignSowing(sowing)}
                  >
                    Assign Chamber
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {assignSowing && (
        <AssignChamberDialog
          sowing={assignSowing}
          open={!!assignSowing}
          onOpenChange={(open) => {
            if (!open) setAssignSowing(null);
          }}
          onSuccess={() => {
            setAssignSowing(null);
            sowingsQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export const RecordGerminationPage = () => {
  const instructionsQuery = useSowingInstructionsByStatus([2, 13]); // Sown + PartiallySown
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedSowing, setSelectedSowing] = useState<SowingDto | null>(null);
  const [selectedInstruction, setSelectedInstruction] =
    useState<SowingInstruction | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleRecordGermination = (sowing: SowingDto) => {
    // Find the parent instruction for the dialog
    const instruction = (instructionsQuery.data ?? []).find(
      (i) => i.id === sowing.sowingInstructionId
    );
    if (instruction) {
      setSelectedInstruction(instruction);
      setSelectedSowing(sowing);
      setDialogOpen(true);
    }
  };

  const handleDialogSuccess = () => {
    setDialogOpen(false);
    setSelectedInstruction(null);
    setSelectedSowing(null);
    instructionsQuery.refetch();
  };

  if (instructionsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p>Loading sowing instructions...</p>
      </div>
    );
  }

  if (instructionsQuery.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load sowing instructions</p>
      </div>
    );
  }

  const instructions = instructionsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Record Germination
        </h1>
        <p className="text-gray-500 mt-1">
          Record germination results for sown seeds — expand an instruction to
          see individual sowing events
        </p>
      </div>

      <div className="rounded-lg border bg-white">
        {instructions.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No sowing instructions ready for germination.
          </p>
        ) : (
          <div className="divide-y">
            {instructions.map((inst) => {
              const isExpanded = expandedId === inst.id;
              const statusLabel =
                SOWING_INSTRUCTION_STATUSES.find((s) => s.value === inst.status)?.label ||
                String(inst.status);

              return (
                <div key={inst.id}>
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                    onClick={() =>
                      setExpandedId(isExpanded ? null : inst.id)
                    }
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-medium">
                        {isExpanded ? "▾" : "▸"}
                      </span>
                      <div>
                        <p className="font-medium">
                          {inst.instructionNumber} — {inst.cropTypeName}{" "}
                          {inst.variety}
                        </p>
                        <p className="text-sm text-gray-500">
                          {inst.quantityToSow} trays target
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusLabel === "Partially Sown"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {statusLabel}
                    </span>
                  </button>
                  {isExpanded && (
                    <SowingEventsPanel
                      instruction={inst}
                      onRecordGermination={handleRecordGermination}
                      onFinalized={() => instructionsQuery.refetch()}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedInstruction && (
        <RecordGerminationWithPreviewDialog
          instruction={selectedInstruction}
          sowing={selectedSowing ?? undefined}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={handleDialogSuccess}
        />
      )}
    </div>
  );
};